import { Button, Input, Select, Space, Card, Tooltip, Typography, Divider, Empty } from '../../ui';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { FileUploadField } from '../../components/FileUploadField';

// ── Shared shape (also what the backend stores as JSON) ──
export type SectionKind = 'TEXT' | 'BULLETS' | 'TABLE' | 'KEYVALUE';
export interface DetailSection {
  id: string;
  title: string;
  kind: SectionKind;
  text?: string;
  imageUrl?: string;
  items?: string[];
  columns?: string[];
  rows?: string[][];
  pairs?: { label: string; value: string }[];
}
export interface ProductResource { label: string; url: string; lang?: string }
export interface ProductDetailsState {
  highlights: string[];
  resources: ProductResource[];
  sections: DetailSection[];
  /** Editorial category slug (drives the public site's category filters + pages). Group-level. */
  category?: string | null;
  /** Recommended crop tags (drive the public /crop-solutions pages). Group-level. */
  crops?: string[];
  /** Short type label under the product name on cards (e.g. "Liquid Bio-Fertilizer · PSB"). */
  tagline?: string | null;
  /** One-line "what it does" blurb for product cards. */
  summary?: string | null;
}

// Mirrors the backend PRODUCT_CATEGORIES taxonomy (source of truth: GET /public/categories). The
// server validates the slug on save, so this list only needs to stay roughly in sync for labels.
const CATEGORY_OPTIONS = [
  { value: 'soil-biology', label: 'Soil Biology' },
  { value: 'soil-improvers', label: 'Soil Improvers' },
  { value: 'biostimulants', label: 'Biostimulants' },
  { value: 'plant-strength', label: 'Plant Strength' },
  { value: 'water-management', label: 'Water Management' },
];

const KIND_LABEL: Record<SectionKind, string> = { TEXT: 'Text', BULLETS: 'Checklist / bullets', TABLE: 'Table', KEYVALUE: 'Key – value' };
const LANGS = [
  { value: 'en', label: 'English' }, { value: 'hi', label: 'Hindi' }, { value: 'mr', label: 'Marathi' },
  { value: 'or', label: 'Odia' }, { value: 'bn', label: 'Bengali' },
];
const newId = () => Math.random().toString(36).slice(2, 10);

/**
 * Builder for a product's rich catalogue page: hero highlights, downloadable resources,
 * and ordered "detail sections" of varying shape (text / bullets / table / key-value).
 * Fully controlled via value/onChange so the parent form owns the state.
 */
export function ProductDetailsEditor({ value, onChange }: { value: ProductDetailsState; onChange: (v: ProductDetailsState) => void }) {
  const v = value;
  const patch = (p: Partial<ProductDetailsState>) => onChange({ ...v, ...p });

  // ── sections ──
  const setSection = (id: string, p: Partial<DetailSection>) =>
    patch({ sections: v.sections.map((s) => (s.id === id ? { ...s, ...p } : s)) });
  const addSection = (kind: SectionKind) => {
    const base: DetailSection = { id: newId(), title: KIND_LABEL[kind], kind };
    if (kind === 'BULLETS') base.items = [];
    if (kind === 'TABLE') { base.columns = ['Column 1', 'Column 2']; base.rows = []; }
    if (kind === 'KEYVALUE') base.pairs = [];
    patch({ sections: [...v.sections, base] });
  };
  const removeSection = (id: string) => patch({ sections: v.sections.filter((s) => s.id !== id) });
  const moveSection = (id: string, dir: -1 | 1) => {
    const i = v.sections.findIndex((s) => s.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= v.sections.length) return;
    const arr = [...v.sections];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    patch({ sections: arr });
  };

  return (
    <div>
      {/* Classification + card copy — drives the public marketing site (cards, category filters, crop-solutions) */}
      <Typography.Text strong>Public website — card & classification</Typography.Text>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>Shown on the public product card + detail page — shared by all pack sizes of this product.</div>
      <Space direction="vertical" style={{ display: 'flex', marginBottom: 4 }} size={8}>
        <Space wrap style={{ display: 'flex' }}>
          <Input style={{ width: 300 }} placeholder="Tagline (e.g. Liquid Bio-Fertilizer · PSB)" maxLength={120}
            value={v.tagline ?? ''} onChange={(e) => patch({ tagline: e.target.value })} />
          <Select style={{ width: 260 }} allowClear placeholder="Category (e.g. Soil Biology)" options={CATEGORY_OPTIONS}
            value={v.category || undefined} onChange={(category) => patch({ category: category ?? null })} />
        </Space>
        <Input style={{ width: '100%' }} placeholder="One-line summary shown on the card (e.g. Rebuilds soil biology in the root zone for deep-rooted crops.)"
          maxLength={400} value={v.summary ?? ''} onChange={(e) => patch({ summary: e.target.value })} />
        <Select mode="tags" style={{ width: '100%' }} placeholder="Recommended crops — type and press Enter (e.g. Grape, Cotton, Paddy)"
          value={v.crops ?? []} onChange={(crops) => patch({ crops })} open={false} suffixIcon={null} tokenSeparators={[',']} />
      </Space>

      <Divider style={{ margin: '16px 0' }} />

      {/* Highlights */}
      <Typography.Text strong>Key highlights</Typography.Text>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>The ✓ bullets shown next to the product (type and press Enter for each).</div>
      <Select mode="tags" style={{ width: '100%' }} placeholder="e.g. Restores Soil Biology"
        value={v.highlights} onChange={(val) => patch({ highlights: val })} tokenSeparators={[]} open={false} suffixIcon={null} />

      <Divider style={{ margin: '16px 0' }} />

      {/* Resources */}
      <Typography.Text strong>Resources / data sheets</Typography.Text>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>Downloadable files shown under “Resources”.</div>
      {v.resources.map((r, i) => (
        <Space key={i} align="start" style={{ display: 'flex', marginBottom: 8 }} wrap>
          <Input style={{ width: 240 }} placeholder="Label (e.g. Technical Data Sheet)" value={r.label}
            onChange={(e) => patch({ resources: v.resources.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)) })} />
          <Select style={{ width: 120 }} allowClear placeholder="Language" options={LANGS} value={r.lang || undefined}
            onChange={(lang) => patch({ resources: v.resources.map((x, idx) => (idx === i ? { ...x, lang } : x)) })} />
          <FileUploadField category="product-brochure" preview={false} buttonLabel={r.url ? 'Replace file' : 'Upload file'}
            value={r.url} onChange={(url) => patch({ resources: v.resources.map((x, idx) => (idx === i ? { ...x, url } : x)) })} />
          <Button variant="soft-danger" icon={<Trash2 size={16} />} aria-label="Remove resource" onClick={() => patch({ resources: v.resources.filter((_, idx) => idx !== i) })} />
        </Space>
      ))}
      <Button variant="default" icon={<Plus size={16} />} onClick={() => patch({ resources: [...v.resources, { label: '', url: '' }] })}>Add resource</Button>

      <Divider style={{ margin: '16px 0' }} />

      {/* Sections */}
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Text strong>Detail sections (the accordion on the product page)</Typography.Text>
        <Space>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Add:</span>
          {(['TEXT', 'BULLETS', 'TABLE', 'KEYVALUE'] as SectionKind[]).map((k) => (
            <Button key={k} variant="default" size="sm" icon={<Plus size={16} />} onClick={() => addSection(k)}>{KIND_LABEL[k]}</Button>
          ))}
        </Space>
      </Space>

      {v.sections.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sections yet — add Text / Bullets / Table / Key-value above (e.g. Dosage, The Formula, How to use)." />
      )}

      {v.sections.map((s, i) => (
        <Card key={s.id} style={{ marginBottom: 10 }}
          title={
            <Space>
              <Input style={{ width: 280 }} value={s.title} placeholder="Section title (e.g. Dosage)" onChange={(e) => setSection(s.id, { title: e.target.value })} />
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>{KIND_LABEL[s.kind]}</span>
            </Space>
          }
          extra={
            <Space>
              <Tooltip label="Move up"><Button size="sm" variant="text" icon={<ArrowUp size={16} />} aria-label="Move up" disabled={i === 0} onClick={() => moveSection(s.id, -1)} /></Tooltip>
              <Tooltip label="Move down"><Button size="sm" variant="text" icon={<ArrowDown size={16} />} aria-label="Move down" disabled={i === v.sections.length - 1} onClick={() => moveSection(s.id, 1)} /></Tooltip>
              <Tooltip label="Remove section"><Button size="sm" variant="soft-danger" icon={<Trash2 size={16} />} aria-label="Remove section" onClick={() => removeSection(s.id)} /></Tooltip>
            </Space>
          }
        >
          <SectionBody section={s} onChange={(p) => setSection(s.id, p)} />
        </Card>
      ))}
    </div>
  );
}

function SectionBody({ section: s, onChange }: { section: DetailSection; onChange: (p: Partial<DetailSection>) => void }) {
  if (s.kind === 'TEXT') {
    return (
      <>
        <Input.TextArea rows={4} placeholder="Paragraph text…" value={s.text} onChange={(e) => onChange({ text: e.target.value })} />
        <div style={{ marginTop: 8 }}>
          <FileUploadField category="product-image" preview buttonLabel={s.imageUrl ? 'Replace image' : 'Add image (optional)'}
            value={s.imageUrl} onChange={(url) => onChange({ imageUrl: url })} />
        </div>
      </>
    );
  }
  if (s.kind === 'BULLETS') {
    return (
      <Select mode="tags" style={{ width: '100%' }} placeholder="Type a bullet and press Enter…"
        value={s.items ?? []} onChange={(items) => onChange({ items })} open={false} suffixIcon={null} />
    );
  }
  if (s.kind === 'KEYVALUE') {
    const pairs = s.pairs ?? [];
    return (
      <>
        {pairs.map((p, i) => (
          <Space key={i} style={{ display: 'flex', marginBottom: 6 }}>
            <Input style={{ width: 240 }} placeholder="Label (e.g. Microbes)" value={p.label}
              onChange={(e) => onChange({ pairs: pairs.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)) })} />
            <Input style={{ width: 280 }} placeholder="Value (e.g. 45%)" value={p.value}
              onChange={(e) => onChange({ pairs: pairs.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)) })} />
            <Button variant="soft-danger" icon={<Trash2 size={16} />} aria-label="Remove row" onClick={() => onChange({ pairs: pairs.filter((_, idx) => idx !== i) })} />
          </Space>
        ))}
        <Button variant="default" size="sm" icon={<Plus size={16} />} onClick={() => onChange({ pairs: [...pairs, { label: '', value: '' }] })}>Add row</Button>
      </>
    );
  }
  // TABLE
  const columns = s.columns ?? [];
  const rows = s.rows ?? [];
  const setCell = (ri: number, ci: number, val: string) =>
    onChange({ rows: rows.map((row, idx) => (idx === ri ? row.map((c, cj) => (cj === ci ? val : c)) : row)) });
  const moveRow = (ri: number, dir: -1 | 1) => {
    const j = ri + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[ri], next[j]] = [next[j], next[ri]];
    onChange({ rows: next });
  };
  const insertRowBelow = (ri: number) => {
    const next = rows.slice();
    next.splice(ri + 1, 0, Array.from({ length: columns.length }, () => ''));
    onChange({ rows: next });
  };
  return (
    <>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Columns (type a name and press Enter):</div>
      <Select mode="tags" style={{ width: '100%', marginBottom: 10 }} placeholder="e.g. Crop, Application, Duration, Dose"
        value={columns} onChange={(cols) => onChange({ columns: cols, rows: rows.map((r) => Array.from({ length: cols.length }, (_, i) => r[i] ?? '')) })}
        open={false} suffixIcon={null} />
      {columns.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>{columns.map((c, ci) => <th key={ci} style={{ textAlign: 'left', padding: '4px 6px', fontSize: 12, color: '#8c8c8c' }}>{c}</th>)}<th /></tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {columns.map((_, ci) => (
                    <td key={ci} style={{ padding: 2 }}>
                      <Input size="sm" value={row[ci] ?? ''} onChange={(e) => setCell(ri, ci, e.target.value)} />
                    </td>
                  ))}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Button size="sm" variant="text" icon={<ArrowUp size={16} />} disabled={ri === 0} title="Move row up" onClick={() => moveRow(ri, -1)} />
                    <Button size="sm" variant="text" icon={<ArrowDown size={16} />} disabled={ri === rows.length - 1} title="Move row down" onClick={() => moveRow(ri, 1)} />
                    <Button size="sm" variant="text" icon={<Plus size={16} />} title="Add row below" onClick={() => insertRowBelow(ri)} />
                    <Button size="sm" variant="soft-danger" icon={<Trash2 size={16} />} title="Delete row" onClick={() => onChange({ rows: rows.filter((_, idx) => idx !== ri) })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Button size="sm" variant="default" style={{ marginTop: 8 }} icon={<Plus size={16} />} disabled={!columns.length}
        onClick={() => onChange({ rows: [...rows, Array.from({ length: columns.length }, () => '')] })}>Add row</Button>
    </>
  );
}
